import { InfoCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import { isAxiosError } from "axios";
import React, { useMemo, useState } from "react";
import { useGetOrganization } from "../../../../../api/v1/organizations/useGetOrganization";
import { useGetPaymentTypeOptions } from "../../../../../hooks/useGetPaymentTypeOptions";
import { isReceiptsModuleEnabled } from "../../../../../utils/isReceiptsModuleEnabled";
import Grid from "../../../../atoms/Grid";
import { convertToTitleCase, formatCurrency, formatDateByType } from "../../../../libs/utils";
import { ButtonGroup, ButtonGroupProps } from "../../../../molecules/Button/ButtonGroup";
import { usePendingRequestsActions } from "../../../PendingApprovalRequets/api/useRefreshPendingRequests";
import { useInvoiceActionNavigation } from "../../hooks/useInvoiceActionNavigation";
import { postReceiptRecallRequest, postUpdateRecallReceiptRequest } from "../../Receipts/api/receiptActions";
import { Invoice } from "../types";
import { RecallChequeRequest, useRecallChequeRequest, useUpdateRecallChequeRequest } from "./api";
import { RecallChequeForm } from "./RecallChequeForm";
import { ReplacementChequeForm } from "./ReplacementChequeForm";
import { useSearchParams } from "react-router-dom";

type ReceiptRow = {
  subPdcId?: string;
  receiptNo: string | number;
  chequeNo: string | number;
  bankName: string;
  paidDate: string;
  amountToPay: number;
  status: string;
  amount?: number;
};

type RecallChequeProps = {
  invoice?: Invoice;
  actionType?: "apply" | "approveReject";
  onClose?: () => void;
  onSuccess?: () => void;
};

export type RecallSubmitParams = {
  recallType: RecallChequeRequest["recallType"];
  recallReason?: string;
  waiveOff?: boolean;
  modeOfPayment?: string;
  amountToPay?: number;
  documentNo?: string;
  bankName?: string;
  paidDate?: string;
  depositDate?: string;
  depositTo?: string;
  depositToBank?: string;
};

export const RecallCheque = (props: RecallChequeProps) => {
  const { invoice, actionType, onClose, onSuccess } = props;
  const { organization } = useGetOrganization();
  const isReceiptContext = isReceiptsModuleEnabled(organization);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const paymentReceiptId = searchParams.get("paymentReceiptId");
  const receiptInvoice = invoice as any;
  const resolvedBankName =
    receiptInvoice?.paymentDetails?.bankName ||
    invoice?.bankName ||
    receiptInvoice?.depositToBank ||
    receiptInvoice?.beneficiaryBank ||
    receiptInvoice?.lessorBankName ||
    receiptInvoice?.recallChequeRequest?.bankName ||
    "";
  const [receiptFlowPending] = useState(false);
  const usageType =
    invoice?.propertyDetails?.usagesType ||
    (invoice as any)?.propertyDetails?.usageType ||
    (invoice as any)?.usageType ||
    "";
  const depositTo =
    invoice?.depositTo || invoice?.beneficiaryName || (invoice as any)?.lessorName || (invoice as any)?.ownerName || "";
  const depositToBank =
    (invoice as any)?.depositToBank || (invoice as any)?.beneficiaryBank || (invoice as any)?.lessorBankName || "";
  const { refreshPendingRequests } = usePendingRequestsActions();
  const { goTo, pdcId: serachParampdcId } = useInvoiceActionNavigation();
  const isApprovalPending = actionType === "approveReject";

  const getClearanceStatus = (isCleared?: boolean) => (isCleared ? "Cleared" : "Not Cleared");

  const data: ReceiptRow[] = useMemo(() => {
    if (!invoice) return [];
    const subPdcs = invoice?.subPdcs ?? [];
    const bankName = resolvedBankName;
    if (subPdcs.length > 0) {
      return subPdcs.map((subPdc) => ({
        ...(subPdc as any),
        subPdcId: subPdc?.subPdcId,
        receiptNo: subPdc?.receiptNo ?? "",
        chequeNo: subPdc?.documentNo ?? "",
        bankName: (subPdc as any)?.bankName ?? bankName,
        paidDate: subPdc?.paidDate,
        amountToPay: subPdc?.amount,
        status: getClearanceStatus(subPdc?.isCleared),
      }));
    }
    return [
      {
        receiptNo: invoice?.receiptNo ?? "",
        chequeNo: invoice?.documentNo ?? "",
        bankName: bankName,
        paidDate: invoice?.paidDate,
        amountToPay: invoice?.totalAmount,
        status: getClearanceStatus(invoice?.isCleared),
      },
    ];
  }, [invoice, resolvedBankName]);

  const isSingleRow = !isApprovalPending && data.length === 1;

  const initialSelectedReceipt: ReceiptRow | null = (() => {
    if (actionType && invoice) {
      if (isReceiptContext) {
        return {
          receiptNo: invoice.receiptNo ?? "",
          chequeNo: receiptInvoice?.paymentDetails?.chequeNo ?? receiptInvoice?.paymentDetails?.documentNo ?? "",
          bankName: receiptInvoice?.paymentDetails?.bankName ?? "",
          paidDate: receiptInvoice?.chequeDate ?? "",
          amountToPay: invoice?.totalAmount ?? 0,
          status: "",
        };
      }

      return {
        receiptNo: invoice.receiptNo ?? "",
        chequeNo: invoice?.recallChequeRequest?.chequeNumber ?? invoice?.recallChequeRequest?.documentNo ?? "",
        bankName: invoice?.recallChequeRequest?.bankName ?? "",
        paidDate: invoice?.recallChequeRequest?.paidDate ?? "",
        amountToPay: invoice?.recallChequeRequest?.amountToPay ?? 0,
        status: "",
      };
    }

    if (isSingleRow) {
      return data[0];
    }

    return null;
  })();
  const [step, setStep] = useState<1 | 2 | 3>(isApprovalPending || isSingleRow ? 2 : 1);
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptRow | null>(initialSelectedReceipt);

  const { paymentTypesOptions } = useGetPaymentTypeOptions({
    usageType: invoice?.propertyDetails?.usagesType,
    configKey: "masterPaymentTypeAttributesWithVat",
  });

  const recallFee = paymentTypesOptions.find((opt) => opt.value === "recall_fee")?.defaultAmount ?? 0;

  const onError = (error: unknown) => {
    if (isAxiosError(error)) message.error(error?.response?.data?.error);
  };

  const { recallChequeRequest, isPendingRecallChequeRequest } = useRecallChequeRequest({ onSuccess, onError });

  const { updateRecallChequeRequest, isPendingUpdateRecallChequeRequest } = useUpdateRecallChequeRequest({
    onSuccess: async () => {
      onSuccess?.();
      await refreshPendingRequests();
      if (isReceiptContext) {
        goTo("/receipts", { replace: true });
      } else {
        serachParampdcId && goTo("/invoicing", { replace: true });
      }
    },
    onError,
  });

  const handleApprove = async () => {
    if (isReceiptContext) {
      try {
        setReceiptLoading(true);
        await postUpdateRecallReceiptRequest({
          paymentReceiptId: invoice.paymentReceiptId,
          status: "approved",
        });
        await refreshPendingRequests();
        if (isReceiptContext) {
          paymentReceiptId && goTo("/receipts", { replace: true });
        } else {
          serachParampdcId && goTo("/invoicing", { replace: true });
        }
        onSuccess?.();
      } catch (e) {
        onError(e);
      }
      setReceiptLoading(false);
      return;
    }
    if (!invoice?.pdcId) return;
    await updateRecallChequeRequest({ pdcId: invoice.pdcId, status: "approved" });
  };

  const handleReject = async () => {
    if (isReceiptContext) {
      try {
        setReceiptLoading(true);
        await postUpdateRecallReceiptRequest({
          paymentReceiptId: invoice.paymentReceiptId,
          status: "rejected",
        });
        await refreshPendingRequests();
        if (paymentReceiptId) goTo("/receipts", { replace: true });
        onSuccess?.();
      } catch (e) {
        onError(e);
      }
      setReceiptLoading(false);
      return;
    }
    if (!invoice?.pdcId) return;
    await updateRecallChequeRequest({ pdcId: invoice.pdcId, status: "rejected" });
  };

  const isPartial = invoice?.subPdcs && invoice?.subPdcs?.length > 0;

  const handleRecallSubmit = async (params: RecallSubmitParams) => {
    if (!selectedReceipt) return;

    if (isReceiptContext) {
      try {
        setReceiptLoading(true);
        const amountValue = Number(params.amountToPay ?? selectedReceipt.amountToPay ?? invoice.totalAmount ?? 0);
        const chequeNo = String(invoice.documentNo ?? selectedReceipt.chequeNo ?? "");
        const receiptBody: Parameters<typeof postReceiptRecallRequest>[0] = {
          paymentReceiptId: invoice.paymentReceiptId,
          chequeNumber: chequeNo,
          amountToPay: Number.isFinite(amountValue) ? amountValue : 0,
          bankName: String(params.bankName ?? invoice.bankName ?? selectedReceipt.bankName ?? ""),
          paidDate: String(params.paidDate ?? selectedReceipt.paidDate ?? ""),
          recallType: params.recallType,
          recallReason: params.recallReason,
        };
        if (isPartial) {
          receiptBody.subPdcId = selectedReceipt.subPdcId ? String(selectedReceipt.subPdcId) : "";
          receiptBody.subChequeNumber = String(selectedReceipt.chequeNo ?? "");
        }
        if (params.recallType === "replaceWithNewReceipt") {
          receiptBody.modeOfPayment = params.modeOfPayment;
          receiptBody.documentNo = params.documentNo;
          receiptBody.depositDate = params.depositDate;
          receiptBody.depositTo = params.depositTo;
          receiptBody.depositToBank = params.depositToBank;
        }
        await postReceiptRecallRequest(receiptBody);
        message.success("Recall request submitted");
        await refreshPendingRequests();
        onSuccess?.();
      } catch (e) {
        onError(e);
      }
      setReceiptLoading(false);
      return;
    }

    if (!invoice?.pdcId) return;

    const payload: RecallChequeRequest = {
      pdcId: invoice.pdcId!,
      chequeNumber: String(invoice.documentNo ?? ""),
      amountToPay: params.amountToPay,
      bankName: params.bankName,
      paidDate: params.paidDate,
      recallType: params.recallType,
      recallReason: params.recallReason,
      ...(params.waiveOff && { waiveOff: params.waiveOff }),
      ...(isPartial && {
        subPdcId: selectedReceipt.subPdcId,
        subChequeNumber: String(selectedReceipt.chequeNo),
      }),
      ...(params.recallType === "replaceWithNewReceipt" && {
        modeOfPayment: params.modeOfPayment,
        amountToPay: params.amountToPay,
        documentNo: params.documentNo,
        bankName: params.bankName,
        paidDate: params.paidDate,
        depositDate: params.depositDate,
        depositTo: params.depositTo,
        depositToBank: params.depositToBank,
      }),
    };

    await recallChequeRequest(payload);
    await refreshPendingRequests();
  };

  const columns = [
    {
      field: "receiptNo",
      headerName: "Receipt No",
      flex: 1,
      maxWidth: 170,
      checkboxSelection: ({ data }) => !Boolean(data?.isCleared),
    },
    { field: "chequeNo", headerName: "Cheque No", flex: 1, maxWidth: 130 },
    {
      field: "bankName",
      headerName: "Bank",
      flex: 1,
      maxWidth: 220,
      valueFormatter: ({ value }) => convertToTitleCase(value),
    },
    {
      field: "paidDate",
      headerName: "Cheque Date",
      flex: 1,
      maxWidth: 140,
      valueFormatter: ({ value }) => formatDateByType(value),
    },
    {
      field: "amountToPay",
      headerName: "Amount",
      flex: 1,
      maxWidth: 170,
      type: "rightAligned",
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    { field: "status", headerName: "Status", flex: 1, maxWidth: 130 },
  ];

  const handleContinue = () => {
    if (!selectedReceipt) return;
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedReceipt(null);
  };

  const primaryButton: ButtonGroupProps["primaryButton"] = {
    label: "Continue",
    onClick: handleContinue,
    disabled: !selectedReceipt,
  };

  const secondaryButton: ButtonGroupProps["secondaryButton"] =
    step === 1 ? undefined : { label: "Back", onClick: onClose };

  if (step === 3 && selectedReceipt) {
    return (
      <ReplacementChequeForm
        selectedReceipt={selectedReceipt}
        usageType={usageType}
        depositTo={depositTo}
        depositToBank={depositToBank}
        recallFee={recallFee}
        onCancel={() => setStep(2)}
        onSubmit={handleRecallSubmit}
        isPending={isPendingRecallChequeRequest || receiptFlowPending || receiptLoading}
      />
    );
  }

  if (step === 2 && selectedReceipt) {
    return (
      <RecallChequeForm
        selectedReceipt={selectedReceipt}
        invoice={invoice ?? ([] as any)}
        recallFee={recallFee}
        usageType={usageType}
        isReceiptContext={isReceiptContext}
        depositTo={depositTo}
        isSingleRow={isSingleRow}
        onCancel={isApprovalPending ? onClose : handleBack}
        onSubmit={handleRecallSubmit}
        onReplace={() => setStep(3)}
        onApprove={isApprovalPending ? handleApprove : undefined}
        onReject={isApprovalPending ? handleReject : undefined}
        isLoading={
          isPendingRecallChequeRequest || isPendingUpdateRecallChequeRequest || receiptFlowPending || receiptLoading
        }
        isApprovalPending={isApprovalPending}
      />
    );
  }

  return (
    <div>
      {(invoice?.subPdcs?.length ?? 0) > 1 && (
        <p style={{ margin: 0, paddingTop: 10 }}>This invoice has multiple receipts. Select a receipt to recall:</p>
      )}
      <Grid
        className="pl-0 pr-0"
        columns={columns}
        data={data}
        rowSelection="single"
        onSelectionChanged={(event) => {
          const rows = event.api.getSelectedRows() as ReceiptRow[];
          setSelectedReceipt(rows[0] ?? null);
        }}
      />
      <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#999", fontStyle: "italic", fontSize: 13 }}>
        <InfoCircleOutlined />
        Cleared cheques cannot be recalled.
      </p>
      <ButtonGroup primaryButton={primaryButton} secondaryButton={secondaryButton} />
    </div>
  );
};
